import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContextLazy'
import { supabase } from '../../lib/supabase-auth'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
  redirectTo?: string
  requireClientUser?: boolean
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [],
  redirectTo = '/login',
  requireClientUser = false
}) => {
  const { user, userProfile, loading } = useAuth()
  const location = useLocation()
  const [isClientUser, setIsClientUser] = useState<boolean | null>(null)
  const [checkingClientStatus, setCheckingClientStatus] = useState(requireClientUser)
  
  // Check if MFA is verified
  const mfaVerified = sessionStorage.getItem('mfa_verified') === 'true'

  useEffect(() => {
    const checkClientStatus = async () => {
      if (requireClientUser && user) {
        try {
          const { data, error } = await supabase
            .from('client_users')
            .select('id')
            .eq('auth_user_id', user.id)
            .single()
          
          setIsClientUser(!!data && !error)
        } catch (err) {
          setIsClientUser(false)
        } finally {
          setCheckingClientStatus(false)
        }
      }
    }

    checkClientStatus()
  }, [user, requireClientUser])

  if (loading || checkingClientStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Check if MFA is required but not verified
  if (user && !mfaVerified) {
    // Check if we're already on the MFA setup page to avoid redirect loops
    if (location.pathname === '/mfa-setup' || location.pathname === '/mfa-verify') {
      return <>{children}</>
    }
    
    // Check if we have a bypass flag from trusted device login
    const bypassTrusted = sessionStorage.getItem('mfa_bypass_trusted') === 'true'
    if (bypassTrusted) {
      // Trusted device login in progress, allow access
      return <>{children}</>
    }
    
    // CRITICAL FIX: Check if there's a pending MFA setup session from LoginForm
    // This means the user is in the middle of MFA verification on the login page
    const pendingMFASetup = sessionStorage.getItem('pending_mfa_setup')
    if (pendingMFASetup) {
      try {
        const pending = JSON.parse(pendingMFASetup)
        // Check if the session is recent (within last 5 minutes)
        const isRecent = Date.now() - pending.timestamp < 5 * 60 * 1000
        
        if (isRecent) {
          // User has an active MFA flow from LoginForm
          // Check if they have MFA configured to determine where to send them
          const hasMFA = !!pending.session?.user?.user_metadata?.totp_secret ||
                         !!pending.session?.user?.user_metadata?.mfa_enabled ||
                         !!pending.session?.user?.user_metadata?.two_factor_enabled
          
          if (hasMFA) {
            // User has MFA - they should verify, NOT set up
            // Don't redirect them away from login where the verification form is
            if (location.pathname === '/login') {
              return <>{children}</>
            }
            // If they're trying to access another protected route, send them to verify
            return <Navigate to="/mfa-verify" state={{ from: location }} replace />
          } else {
            // User doesn't have MFA - send them to setup
            return <Navigate to="/mfa-setup" state={{ from: location }} replace />
          }
        }
      } catch (e) {
        // Invalid JSON, continue with default behavior
      }
    }
    
    // Default: redirect to MFA setup
    return <Navigate to="/mfa-setup" state={{ from: location }} replace />
  }

  // Check if client user is required
  if (requireClientUser && isClientUser === false) {
    return <Navigate to="/client-portal/login" replace />
  }

  // Check role requirements for non-client routes
  if (!requireClientUser && requiredRoles.length > 0 && userProfile && !requiredRoles.includes(userProfile.role)) {
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}